import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { Subscription, forkJoin } from 'rxjs';

import { ResearchDevelopmentProjectOverview, ResearchDevelopmentTimesheetReportRow } from '../../models/Financial/ResearchDevelopment';
import { ProjectInfoModalComponent } from '../../projects/project-info-modal/project-info-modal.component';
import { DateNavigationOptions, DateNavigationType, SelectedDateClass } from '../../shared/date-navigation/date-navigation.component';
import { FinancialService } from '../financial.service';

interface ResearchDevelopmentMonthlySummary {
    monthKey: string;
    monthLabel: string;
    projectCount: number;
    employeeCount: number;
    timesheetRows: number;
    totalHours: number;
    timesheetCost: number;
    purchaseInvoiceCost: number;
    totalCost: number;
    researchDevelopmentCost: number;
}

interface ResearchDevelopmentMonthlyProjectSummary extends ResearchDevelopmentMonthlySummary {
    projectId: string;
    projectTitle: string;
    percentage: number;
}

@Component({
    selector: 'app-research-development-overview',
    templateUrl: './research-development-overview.component.html',
    styleUrls: ['./research-development-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ResearchDevelopmentOverviewComponent implements OnInit, OnDestroy {
    projectDataSource = new MatTableDataSource<ResearchDevelopmentProjectOverview>();
    reportDataSource = new MatTableDataSource<ResearchDevelopmentTimesheetReportRow>();
    monthlyDataSource = new MatTableDataSource<ResearchDevelopmentMonthlySummary>();
    monthlyProjectDataSource = new MatTableDataSource<ResearchDevelopmentMonthlyProjectSummary>();
    projectColumns = ['project', 'percentage', 'totalHours', 'timesheetCost', 'purchaseInvoiceCost', 'totalCost', 'researchDevelopmentCost'];
    monthlyColumns = [
        'month',
        'projects',
        'employees',
        'timesheetRows',
        'totalHours',
        'timesheetCost',
        'purchaseInvoiceCost',
        'totalCost',
        'researchDevelopmentCost',
    ];
    monthlyProjectColumns = [
        'month',
        'project',
        'percentage',
        'employees',
        'timesheetRows',
        'totalHours',
        'timesheetCost',
        'purchaseInvoiceCost',
        'totalCost',
        'researchDevelopmentCost',
    ];
    reportColumns = [
        'period',
        'project',
        'costType',
        'invoiceNumber',
        'employee',
        'description',
        'hours',
        'hourlyCost',
        'totalCost',
        'percentage',
        'researchDevelopmentCost',
    ];

    dateNavigationOptions: DateNavigationOptions;
    loading = false;
    searchText = '';
    selectedTab: 'projects' | 'months' | 'monthProjects' | 'report' = 'projects';

    private subscriptions = new Subscription();
    private projectSort: MatSort;
    private reportSort: MatSort;
    private monthlySort: MatSort;
    private monthlyProjectSort: MatSort;

    constructor(private financialService: FinancialService, private modalService: NgbModal) {}

    @ViewChild('projectSort') set setProjectSort(sort: MatSort) {
        this.projectSort = sort;
        this.projectDataSource.sort = sort;
    }

    @ViewChild('reportSort') set setReportSort(sort: MatSort) {
        this.reportSort = sort;
        this.reportDataSource.sort = sort;
    }

    @ViewChild('monthlySort') set setMonthlySort(sort: MatSort) {
        this.monthlySort = sort;
        this.monthlyDataSource.sort = sort;
    }

    @ViewChild('monthlyProjectSort') set setMonthlyProjectSort(sort: MatSort) {
        this.monthlyProjectSort = sort;
        this.monthlyProjectDataSource.sort = sort;
    }

    ngOnInit(): void {
        this.dateNavigationOptions = {
            dateNavigationType: DateNavigationType.YEAR,
            selectedDate: new Date(),
            selectedDateClass: SelectedDateClass.SMALL,
        };

        this.projectDataSource.filterPredicate = (row, filter) => this.matchesProjectFilter(row, filter);
        this.reportDataSource.filterPredicate = (row, filter) => this.matchesReportFilter(row, filter);
        this.monthlyDataSource.filterPredicate = (row, filter) => this.matchesMonthlyFilter(row, filter);
        this.monthlyProjectDataSource.filterPredicate = (row, filter) => this.matchesMonthlyProjectFilter(row, filter);
        this.projectDataSource.sortingDataAccessor = this.buildProjectSortingAccessor();
        this.reportDataSource.sortingDataAccessor = this.buildReportSortingAccessor();
        this.monthlyDataSource.sortingDataAccessor = this.buildMonthlySortingAccessor();
        this.monthlyProjectDataSource.sortingDataAccessor = this.buildMonthlyProjectSortingAccessor();
        this.loadData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    updateSelectedDate(): void {
        this.loadData();
    }

    applyFilter(event: Event): void {
        this.searchText = (event.target as HTMLInputElement).value.trim().toLowerCase();
        this.projectDataSource.filter = this.searchText;
        this.reportDataSource.filter = this.searchText;
        this.monthlyDataSource.filter = this.searchText;
        this.monthlyProjectDataSource.filter = this.searchText;
    }

    openProject(projectId: string): void {
        const modalRef = this.modalService.open(ProjectInfoModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });
        modalRef.componentInstance.projectUuid = projectId;
        modalRef.componentInstance.openFinancialTab = true;
    }

    getTotalHours(): number {
        return this.projectDataSource.filteredData.reduce((sum, row) => sum + (row.totalHours || 0), 0);
    }

    getTotalCost(): number {
        return this.projectDataSource.filteredData.reduce((sum, row) => sum + (row.totalCost || 0), 0);
    }

    getTotalResearchDevelopmentCost(): number {
        return this.projectDataSource.filteredData.reduce((sum, row) => sum + (row.researchDevelopmentCost || 0), 0);
    }

    exportReportCsv(): void {
        const csvRows: any[][] = [];
        const projectRows = this.getProjectExportRows();
        const monthlyRows = this.monthlyDataSource.filteredData;
        const monthlyProjectRows = this.monthlyProjectDataSource.filteredData;
        const detailRows = this.reportDataSource.filteredData;

        csvRows.push(['Onderzoek en ontwikkeling rapport', this.selectedYear]);
        csvRows.push(['Gegenereerd op', moment().format('DD/MM/YYYY HH:mm')]);
        csvRows.push(['Zoekfilter', this.searchText || '-']);
        csvRows.push([]);
        csvRows.push(['Samenvatting']);
        csvRows.push(['O&O projecten', this.projectDataSource.filteredData.length]);
        csvRows.push(['Medewerkers', this.getUniqueEmployeeCount(detailRows)]);
        csvRows.push(['Timesheetregels', detailRows.length]);
        csvRows.push(['Uren', this.getTotalHours()]);
        csvRows.push(['Totale kost', this.getTotalCost()]);
        csvRows.push(['O&O bedrag', this.getTotalResearchDevelopmentCost()]);
        csvRows.push([]);

        csvRows.push(['Per project']);
        csvRows.push(['Project', 'O&O %', 'Maanden', 'Medewerkers', 'Regels', 'Uren', 'Timesheetkost', 'Aankoopfacturen', 'Totale kost', 'O&O bedrag']);
        projectRows.forEach((row) => {
            csvRows.push([
                row.projectTitle,
                row.percentage,
                row.monthCount,
                row.employeeCount,
                row.timesheetRows,
                row.totalHours,
                row.timesheetCost,
                row.purchaseInvoiceCost,
                row.totalCost,
                row.researchDevelopmentCost,
            ]);
        });
        csvRows.push([]);

        csvRows.push(['Per maand']);
        csvRows.push(['Maand', 'Projecten', 'Medewerkers', 'Regels', 'Uren', 'Timesheetkost', 'Aankoopfacturen', 'Totale kost', 'O&O bedrag']);
        monthlyRows.forEach((row) => {
            csvRows.push([
                row.monthLabel,
                row.projectCount,
                row.employeeCount,
                row.timesheetRows,
                row.totalHours,
                row.timesheetCost,
                row.purchaseInvoiceCost,
                row.totalCost,
                row.researchDevelopmentCost,
            ]);
        });
        csvRows.push([]);

        csvRows.push(['Per maand per project']);
        csvRows.push(['Maand', 'Project', 'O&O %', 'Medewerkers', 'Regels', 'Uren', 'Timesheetkost', 'Aankoopfacturen', 'Totale kost', 'O&O bedrag']);
        monthlyProjectRows.forEach((row) => {
            csvRows.push([
                row.monthLabel,
                row.projectTitle,
                row.percentage,
                row.employeeCount,
                row.timesheetRows,
                row.totalHours,
                row.timesheetCost,
                row.purchaseInvoiceCost,
                row.totalCost,
                row.researchDevelopmentCost,
            ]);
        });
        csvRows.push([]);

        csvRows.push(['Detail regels']);
        csvRows.push([
            'Period',
            'Project',
            'Type',
            'Invoice number',
            'Employee',
            'Description',
            'Hours',
            'Hourly cost',
            'Total cost',
            'O&O percentage',
            'O&O cost',
        ]);
        detailRows.forEach((row) => {
            csvRows.push([
                this.formatMonth(row.period),
                row.projectTitle,
                this.formatCostType(row.costType),
                row.invoiceNumber || '',
                row.employeeName,
                row.description || '',
                row.hours,
                row.hourlyCost,
                row.totalCost,
                row.percentage,
                row.researchDevelopmentCost,
            ]);
        });

        const csv = csvRows.map((cells) => cells.map((cell) => this.csvCell(cell)).join(';')).join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `research-development-full-report-${this.selectedYear}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    exportReportPdf(): void {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            return;
        }

        printWindow.document.open();
        printWindow.document.write(this.buildPrintableReportHtml());
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 250);
    }

    get selectedYear(): number {
        return this.dateNavigationOptions.selectedDate.getFullYear();
    }

    private loadData(): void {
        this.loading = true;
        const year = this.selectedYear;
        this.subscriptions.add(
            forkJoin({
                projects: this.financialService.getResearchDevelopmentProjectOverview(year),
                report: this.financialService.getResearchDevelopmentTimesheetReport(year),
            }).subscribe({
                next: ({ projects, report }) => {
                    this.projectDataSource.data = (projects || []).map((project) => ({
                        ...project,
                        timesheetCost: project.timesheetCost || 0,
                        purchaseInvoiceCost: project.purchaseInvoiceCost || 0,
                    }));
                    this.reportDataSource.data = report || [];
                    this.projectDataSource.sort = this.projectSort;
                    this.reportDataSource.sort = this.reportSort;
                    this.rebuildReportSummaries(report || []);
                    this.monthlyDataSource.sort = this.monthlySort;
                    this.monthlyProjectDataSource.sort = this.monthlyProjectSort;
                    this.projectDataSource.filter = this.searchText;
                    this.reportDataSource.filter = this.searchText;
                    this.monthlyDataSource.filter = this.searchText;
                    this.monthlyProjectDataSource.filter = this.searchText;
                    this.loading = false;
                },
                error: () => {
                    this.projectDataSource.data = [];
                    this.reportDataSource.data = [];
                    this.monthlyDataSource.data = [];
                    this.monthlyProjectDataSource.data = [];
                    this.loading = false;
                },
            }),
        );
    }

    private matchesProjectFilter(row: ResearchDevelopmentProjectOverview, filter: string): boolean {
        if (!filter) return true;
        return (row.projectTitle || '').toLowerCase().includes(filter);
    }

    private matchesReportFilter(row: ResearchDevelopmentTimesheetReportRow, filter: string): boolean {
        if (!filter) return true;
        return [row.projectTitle, row.employeeName, row.description, row.period, row.costType, row.invoiceNumber, this.formatMonth(row.period)].some((value) =>
            (value || '').toString().toLowerCase().includes(filter),
        );
    }

    private matchesMonthlyFilter(row: ResearchDevelopmentMonthlySummary, filter: string): boolean {
        if (!filter) return true;
        return [row.monthLabel, row.monthKey].some((value) => (value || '').toString().toLowerCase().includes(filter));
    }

    private matchesMonthlyProjectFilter(row: ResearchDevelopmentMonthlyProjectSummary, filter: string): boolean {
        if (!filter) return true;
        return [row.monthLabel, row.monthKey, row.projectTitle].some((value) => (value || '').toString().toLowerCase().includes(filter));
    }

    private buildProjectSortingAccessor(): (item: ResearchDevelopmentProjectOverview, property: string) => string | number {
        return (item, property) => {
            switch (property) {
                case 'project':
                    return (item.projectTitle || '').toLowerCase();
                default:
                    return (item as any)[property] || 0;
            }
        };
    }

    private buildReportSortingAccessor(): (item: ResearchDevelopmentTimesheetReportRow, property: string) => string | number {
        return (item, property) => {
            switch (property) {
                case 'period':
                    return this.getMonthKey(item.period);
                case 'project':
                    return (item.projectTitle || '').toLowerCase();
                case 'employee':
                    return (item.employeeName || '').toLowerCase();
                case 'costType':
                    return this.formatCostType(item.costType).toLowerCase();
                case 'invoiceNumber':
                    return (item.invoiceNumber || '').toLowerCase();
                default:
                    return (item as any)[property] || 0;
            }
        };
    }

    private buildMonthlySortingAccessor(): (item: ResearchDevelopmentMonthlySummary, property: string) => string | number {
        return (item, property) => {
            switch (property) {
                case 'month':
                    return item.monthKey;
                case 'projects':
                    return item.projectCount;
                case 'employees':
                    return item.employeeCount;
                default:
                    return (item as any)[property] || 0;
            }
        };
    }

    private buildMonthlyProjectSortingAccessor(): (item: ResearchDevelopmentMonthlyProjectSummary, property: string) => string | number {
        return (item, property) => {
            switch (property) {
                case 'month':
                    return item.monthKey;
                case 'project':
                    return (item.projectTitle || '').toLowerCase();
                case 'employees':
                    return item.employeeCount;
                default:
                    return (item as any)[property] || 0;
            }
        };
    }

    private rebuildReportSummaries(reportRows: ResearchDevelopmentTimesheetReportRow[]): void {
        const monthlyMap = new Map<string, ResearchDevelopmentMonthlySummary & { projectIds: Set<string>; employeeIds: Set<string> }>();
        const monthlyProjectMap = new Map<string, ResearchDevelopmentMonthlyProjectSummary & { employeeIds: Set<string> }>();

        reportRows.forEach((row) => {
            const monthKey = this.getMonthKey(row.period);
            const monthLabel = this.formatMonth(row.period);
            const projectId = row.projectId || 'unknown';
            const employeeId = row.employeeId || row.employeeName || 'unknown';

            if (!monthlyMap.has(monthKey)) {
                monthlyMap.set(monthKey, {
                    monthKey,
                    monthLabel,
                    projectCount: 0,
                    employeeCount: 0,
                    timesheetRows: 0,
                    totalHours: 0,
                    timesheetCost: 0,
                    purchaseInvoiceCost: 0,
                    totalCost: 0,
                    researchDevelopmentCost: 0,
                    projectIds: new Set<string>(),
                    employeeIds: new Set<string>(),
                });
            }
            const monthRow = monthlyMap.get(monthKey);
            monthRow.projectIds.add(projectId);
            if (this.isTimesheetRow(row) && employeeId) monthRow.employeeIds.add(employeeId);
            monthRow.timesheetRows += 1;
            monthRow.totalHours += row.hours || 0;
            if (this.isPurchaseInvoiceRow(row)) {
                monthRow.purchaseInvoiceCost += row.totalCost || 0;
            } else {
                monthRow.timesheetCost += row.totalCost || 0;
            }
            monthRow.totalCost += row.totalCost || 0;
            monthRow.researchDevelopmentCost += row.researchDevelopmentCost || 0;

            const monthProjectKey = `${monthKey}|${projectId}`;
            if (!monthlyProjectMap.has(monthProjectKey)) {
                monthlyProjectMap.set(monthProjectKey, {
                    monthKey,
                    monthLabel,
                    projectId,
                    projectTitle: row.projectTitle || '-',
                    percentage: row.percentage || 0,
                    projectCount: 1,
                    employeeCount: 0,
                    timesheetRows: 0,
                    totalHours: 0,
                    timesheetCost: 0,
                    purchaseInvoiceCost: 0,
                    totalCost: 0,
                    researchDevelopmentCost: 0,
                    employeeIds: new Set<string>(),
                });
            }
            const monthProjectRow = monthlyProjectMap.get(monthProjectKey);
            if (this.isTimesheetRow(row) && employeeId) monthProjectRow.employeeIds.add(employeeId);
            monthProjectRow.timesheetRows += 1;
            monthProjectRow.totalHours += row.hours || 0;
            if (this.isPurchaseInvoiceRow(row)) {
                monthProjectRow.purchaseInvoiceCost += row.totalCost || 0;
            } else {
                monthProjectRow.timesheetCost += row.totalCost || 0;
            }
            monthProjectRow.totalCost += row.totalCost || 0;
            monthProjectRow.researchDevelopmentCost += row.researchDevelopmentCost || 0;
        });

        this.monthlyDataSource.data = Array.from(monthlyMap.values())
            .map((row) => ({
                monthKey: row.monthKey,
                monthLabel: row.monthLabel,
                projectCount: row.projectIds.size,
                employeeCount: row.employeeIds.size,
                timesheetRows: row.timesheetRows,
                totalHours: row.totalHours,
                timesheetCost: row.timesheetCost,
                purchaseInvoiceCost: row.purchaseInvoiceCost,
                totalCost: row.totalCost,
                researchDevelopmentCost: row.researchDevelopmentCost,
            }))
            .sort((a, b) => a.monthKey.localeCompare(b.monthKey));

        this.monthlyProjectDataSource.data = Array.from(monthlyProjectMap.values())
            .map((row) => ({
                monthKey: row.monthKey,
                monthLabel: row.monthLabel,
                projectId: row.projectId,
                projectTitle: row.projectTitle,
                percentage: row.percentage,
                projectCount: row.projectCount,
                employeeCount: row.employeeIds.size,
                timesheetRows: row.timesheetRows,
                totalHours: row.totalHours,
                timesheetCost: row.timesheetCost,
                purchaseInvoiceCost: row.purchaseInvoiceCost,
                totalCost: row.totalCost,
                researchDevelopmentCost: row.researchDevelopmentCost,
            }))
            .sort((a, b) => a.monthKey.localeCompare(b.monthKey) || a.projectTitle.localeCompare(b.projectTitle));
    }

    private getProjectExportRows(): Array<ResearchDevelopmentProjectOverview & { monthCount: number; employeeCount: number; timesheetRows: number }> {
        const detailByProject = new Map<string, { monthKeys: Set<string>; employeeIds: Set<string>; rows: number }>();
        this.reportDataSource.filteredData.forEach((row) => {
            const projectId = row.projectId || 'unknown';
            if (!detailByProject.has(projectId)) {
                detailByProject.set(projectId, {
                    monthKeys: new Set<string>(),
                    employeeIds: new Set<string>(),
                    rows: 0,
                });
            }
            const detail = detailByProject.get(projectId);
            detail.monthKeys.add(this.getMonthKey(row.period));
            if (this.isTimesheetRow(row)) detail.employeeIds.add(row.employeeId || row.employeeName || 'unknown');
            detail.rows += 1;
        });

        return this.projectDataSource.filteredData.map((project) => {
            const detail = detailByProject.get(project.projectId);
            return {
                ...project,
                monthCount: detail?.monthKeys.size || 0,
                employeeCount: detail?.employeeIds.size || 0,
                timesheetRows: detail?.rows || 0,
            };
        });
    }

    private getUniqueEmployeeCount(rows: ResearchDevelopmentTimesheetReportRow[]): number {
        return new Set(
            rows
                .filter((row) => this.isTimesheetRow(row))
                .map((row) => row.employeeId || row.employeeName)
                .filter((value) => !!value),
        ).size;
    }

    private isPurchaseInvoiceRow(row: ResearchDevelopmentTimesheetReportRow): boolean {
        return row?.costType === 'PURCHASE_INVOICE';
    }

    private isTimesheetRow(row: ResearchDevelopmentTimesheetReportRow): boolean {
        return !this.isPurchaseInvoiceRow(row);
    }

    formatCostType(costType: string): string {
        return costType === 'PURCHASE_INVOICE' ? 'Aankoopfactuur' : 'Timesheet';
    }

    private getMonthKey(period: string): string {
        const parsed = moment(period);
        return parsed.isValid() ? parsed.format('YYYY-MM') : period || '-';
    }

    private formatMonth(period: string): string {
        const parsed = moment(period);
        return parsed.isValid() ? parsed.format('MMMM YYYY') : period || '-';
    }

    private formatNumber(value: number, digits = 2): string {
        return new Intl.NumberFormat('nl-BE', {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(value || 0);
    }

    private formatCurrency(value: number): string {
        return new Intl.NumberFormat('nl-BE', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value || 0);
    }

    private buildPrintableReportHtml(): string {
        const projectRows = this.getProjectExportRows();
        const monthlyRows = this.monthlyDataSource.filteredData;
        const monthlyProjectRows = this.monthlyProjectDataSource.filteredData;
        const detailRows = this.reportDataSource.filteredData;

        return `
            <!doctype html>
            <html>
                <head>
                    <title>O&O rapport ${this.selectedYear}</title>
                    <style>
                        body { font-family: Arial, sans-serif; color: #172033; margin: 28px; }
                        h1 { font-size: 24px; margin: 0 0 4px; }
                        h2 { font-size: 16px; margin: 28px 0 10px; border-bottom: 1px solid #d9e1ef; padding-bottom: 6px; }
                        .meta { color: #667085; margin-bottom: 22px; }
                        .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
                        .summary div { border: 1px solid #d9e1ef; padding: 10px; background: #f8fbff; }
                        .summary label { display: block; color: #667085; font-size: 11px; margin-bottom: 4px; }
                        .summary strong { font-size: 16px; }
                        table { width: 100%; border-collapse: collapse; margin-bottom: 16px; page-break-inside: auto; }
                        tr { page-break-inside: avoid; page-break-after: auto; }
                        th, td { border: 1px solid #d9e1ef; padding: 6px 7px; font-size: 10px; vertical-align: top; }
                        th { background: #eef3fb; text-align: left; }
                        td.num, th.num { text-align: right; white-space: nowrap; }
                        .small { font-size: 9px; color: #667085; }
                        @media print {
                            body { margin: 14mm; }
                            h2 { page-break-after: avoid; }
                        }
                    </style>
                </head>
                <body>
                    <h1>Onderzoek en ontwikkeling rapport ${this.selectedYear}</h1>
                    <div class="meta">Gegenereerd op ${moment().format('DD/MM/YYYY HH:mm')}${
            this.searchText ? ` - filter: ${this.escapeHtml(this.searchText)}` : ''
        }</div>
                    <div class="summary">
                        <div><label>O&O projecten</label><strong>${this.projectDataSource.filteredData.length}</strong></div>
                        <div><label>Regels</label><strong>${detailRows.length}</strong></div>
                        <div><label>Uren</label><strong>${this.formatNumber(this.getTotalHours(), 1)}h</strong></div>
                        <div><label>O&O bedrag</label><strong>${this.formatCurrency(this.getTotalResearchDevelopmentCost())}</strong></div>
                    </div>
                    <h2>Per project</h2>
                    ${this.buildHtmlTable(
                        ['Project', 'O&O %', 'Maanden', 'Medewerkers', 'Regels', 'Uren', 'Timesheetkost', 'Aankoopfacturen', 'Totale kost', 'O&O bedrag'],
                        projectRows.map((row) => [
                            this.escapeHtml(row.projectTitle),
                            this.formatNumber(row.percentage, 2) + '%',
                            row.monthCount,
                            row.employeeCount,
                            row.timesheetRows,
                            this.formatNumber(row.totalHours, 2),
                            this.formatCurrency(row.timesheetCost),
                            this.formatCurrency(row.purchaseInvoiceCost),
                            this.formatCurrency(row.totalCost),
                            this.formatCurrency(row.researchDevelopmentCost),
                        ]),
                        [false, true, true, true, true, true, true, true, true, true],
                    )}
                    <h2>Per maand</h2>
                    ${this.buildHtmlTable(
                        ['Maand', 'Projecten', 'Medewerkers', 'Regels', 'Uren', 'Timesheetkost', 'Aankoopfacturen', 'Totale kost', 'O&O bedrag'],
                        monthlyRows.map((row) => [
                            this.escapeHtml(row.monthLabel),
                            row.projectCount,
                            row.employeeCount,
                            row.timesheetRows,
                            this.formatNumber(row.totalHours, 2),
                            this.formatCurrency(row.timesheetCost),
                            this.formatCurrency(row.purchaseInvoiceCost),
                            this.formatCurrency(row.totalCost),
                            this.formatCurrency(row.researchDevelopmentCost),
                        ]),
                        [false, true, true, true, true, true, true, true, true],
                    )}
                    <h2>Per maand per project</h2>
                    ${this.buildHtmlTable(
                        ['Maand', 'Project', 'O&O %', 'Medewerkers', 'Regels', 'Uren', 'Timesheetkost', 'Aankoopfacturen', 'Totale kost', 'O&O bedrag'],
                        monthlyProjectRows.map((row) => [
                            this.escapeHtml(row.monthLabel),
                            this.escapeHtml(row.projectTitle),
                            this.formatNumber(row.percentage, 2) + '%',
                            row.employeeCount,
                            row.timesheetRows,
                            this.formatNumber(row.totalHours, 2),
                            this.formatCurrency(row.timesheetCost),
                            this.formatCurrency(row.purchaseInvoiceCost),
                            this.formatCurrency(row.totalCost),
                            this.formatCurrency(row.researchDevelopmentCost),
                        ]),
                        [false, false, true, true, true, true, true, true, true, true],
                    )}
                    <h2>Detail regels</h2>
                    ${this.buildHtmlTable(
                        ['Periode', 'Project', 'Type', 'Factuur', 'Medewerker', 'Omschrijving', 'Uren', 'Kost/u', 'Totale kost', 'O&O %', 'O&O bedrag'],
                        detailRows.map((row) => [
                            this.escapeHtml(this.formatMonth(row.period)),
                            this.escapeHtml(row.projectTitle),
                            this.escapeHtml(this.formatCostType(row.costType)),
                            this.escapeHtml(row.invoiceNumber || '-'),
                            this.escapeHtml(row.employeeName || '-'),
                            this.escapeHtml(row.description || '-'),
                            this.formatNumber(row.hours, 2),
                            this.formatCurrency(row.hourlyCost),
                            this.formatCurrency(row.totalCost),
                            this.formatNumber(row.percentage, 2) + '%',
                            this.formatCurrency(row.researchDevelopmentCost),
                        ]),
                        [false, false, false, false, false, false, true, true, true, true, true],
                    )}
                </body>
            </html>
        `;
    }

    private buildHtmlTable(headers: string[], rows: any[][], numericColumns: boolean[]): string {
        const headerHtml = headers.map((header, index) => `<th class="${numericColumns[index] ? 'num' : ''}">${this.escapeHtml(header)}</th>`).join('');
        const bodyHtml = rows.length
            ? rows
                  .map((row) => {
                      const cells = row.map((cell, index) => `<td class="${numericColumns[index] ? 'num' : ''}">${cell}</td>`).join('');
                      return `<tr>${cells}</tr>`;
                  })
                  .join('')
            : `<tr><td colspan="${headers.length}" class="small">Geen data.</td></tr>`;
        return `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;
    }

    private escapeHtml(value: any): string {
        const text = value === null || value === undefined ? '' : value.toString();
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }

    private csvCell(value: any): string {
        const text = value === null || value === undefined ? '' : value.toString();
        return `"${text.replace(/"/g, '""')}"`;
    }
}
